/**
 * Entity API Functions
 * Handles all entity-related API calls
 */

import type { Entity, EntitySummary, Transaction } from '@/types/entities';

/**
 * Helper to handle API errors with detailed messages
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new Error(
      `API request failed (${response.status}): ${error.message || error.error || response.statusText}`
    );
  }
  return response.json();
}

/**
 * Fetch all entities
 */
export async function fetchEntities(): Promise<Entity[]> {
  const response = await fetch('/api/entities', {
    credentials: 'include',
  });
  return handleResponse<Entity[]>(response);
}

/**
 * Fetch a single entity by ID
 */
export async function fetchEntity(id: string): Promise<Entity> {
  const response = await fetch(`/api/entities/${id}`, {
    credentials: 'include',
  });
  return handleResponse<Entity>(response);
}

/**
 * Fetch entity summary (includes balance and recent transactions)
 */
export async function fetchEntitySummary(id: string): Promise<EntitySummary> {
  const response = await fetch(`/api/entities/${id}/summary`, {
    credentials: 'include',
  });
  return handleResponse<EntitySummary>(response);
}

/**
 * Fetch all transactions for an entity
 */
export async function fetchEntityTransactions(
  entityId: string,
  options?: {
    limit?: number;
    offset?: number;
    startDate?: Date;
    endDate?: Date;
  }
): Promise<Transaction[]> {
  const params = new URLSearchParams();

  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());
  if (options?.startDate)
    params.set('startDate', options.startDate.toISOString());
  if (options?.endDate) params.set('endDate', options.endDate.toISOString());

  const response = await fetch(
    `/api/entities/${entityId}/transactions?${params}`,
    {
      credentials: 'include',
    }
  );
  return handleResponse<Transaction[]>(response);
}

/**
 * Create a new entity
 */
export async function createEntity(
  entity: Omit<Entity, 'id' | 'createdAt'>
): Promise<Entity> {
  const response = await fetch('/api/entities', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(entity),
  });
  return handleResponse<Entity>(response);
}

/**
 * Update an entity
 */
export async function updateEntity(
  id: string,
  entity: Partial<Omit<Entity, 'id' | 'createdAt'>>
): Promise<Entity> {
  const response = await fetch(`/api/entities/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(entity),
  });
  return handleResponse<Entity>(response);
}

/**
 * Delete an entity
 */
export async function deleteEntity(id: string): Promise<void> {
  const response = await fetch(`/api/entities/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new Error(
      `Failed to delete entity (${response.status}): ${error.message || error.error || response.statusText}`
    );
  }
}
